/**
 * Console filter to reduce noise in development
 * This wraps console methods to filter out verbose logs
 */

import { config } from '@/config/config';

// Patterns to filter out (won't be shown)
const FILTER_PATTERNS = [
  // API Request/Response logs
  /\[API\].*REQUEST:/,
  /\[API\].*SUCCESS:/,
  /\[API getUserProfile\]/,
  /\[API\] \d{4}-\d{2}-\d{2}/,
  
  // Transform and data processing
  /\[transformConversation\]/,
  /\[conversationQueries\]/,
  /\[messages.*\] Transforming/,
  /\[messages.*\] Returning/,
  /\[MessageItem\] Received Props:/,
  /\[messages\.ts transformApiMessage\]/,
  
  // User identity and auth verbose logs
  /\[userIdentity\].*check for/,
  /\[userIdentity\].*MATCHES/,
  /\[userIdentity\].*mapping/,
  /\[AuthContext\].*session token/,
  /\[AuthContext\].*localStorage/,
  
  // Socket and presence
  /\[SocketProvider\].*Disconnecting/,
  /\[SocketProvider\].*Event received/,
  // NOTE: [SocketClient] removed from filter to debug socket connection issues
  /\[PresenceManager\]/,
  /\[Presence\] Backend endpoint not implemented/,
  /updateMyPresence/i,
  /presence\/me/i,
  /Failed to update presence/i,
  
  // RTK Query and Redux
  /\[RTK Query\]/,
  /\[ConvSubscriptions\]/,
  /\[conversationsSlice\]/,
  /\[Slice\] getContactDetails/,
  /\[UserProfileLoader\]/,
  /\[UserProfileSync\]/,
  
  // Component lifecycle and debugging
  /\[ConvDebug\]/,
  /\[useMessagePreviews\]/,
  /\[useContactPrefetch\]/,
  /\[useConversationSocket\]/,
  /\[useContactPrefetchOnMessageLoad\]/,
  /\[useUiConversations\]/,
  /\[SearchBar\]/,
  /\[ConversationTransformer\]/,
  /\[VirtualizedMessageList\]/,
  /Header width:/,
  /\[getOrganizationMembers\]/,
  /\[useTeamSidebarData\]/,
  /\[Navigation\] Changed from/,
  /\[Login\] Starting login/,
  /\[Login\] Calling backend/,
  /\[Login\] Backend response/,
  /\[Login\] Login successful/,
  
  // WebVitals and performance
  /\[Web Vitals\]/,
  /\[Fast Refresh\]/,
  
  // Other verbose logs
  /activeContactDetailSlice\.ts:/,
  /middleware\.ts:/,
  /conversation-debug\.ts:/,
  /hook\.js:/,
  /Updating transform to:/,
  /Initializing global selectedMessageIds/,
  /Current selectedMessageIds:/,
  /\[Visibility\]/,
  /\[OfflineQueue\]/,
  
  // Debug tool messages
  /Initializing debug tools/,
  /Conversation debug ready/,
  /Debug tools ready/,
  /Debug listener for all events/,
  /\[useConversationSocket\]/,
  
  // Additional verbose logs to filter
  /\[AuthContext\] Stytch client initializing/,
  /\[AuthContext\] Initial auth check/,
  /\[AuthContext\] Session authentication failed/,
  /Using cached user profile for getUserProfile/,
  /getUserProfile completed successfully/,
  
  // Stytch 401 errors (expected when using backend auth)
  /401.*stytch/i,
  /stytch.*401/i,
  /Failed to hydrate Stytch SDK/,
  /Stytch SDK hydration failed/,
  /SDK session authenticate failed/,
  /Error revoking Stytch session/,
  /\[ConversationList\] List received/,
  /\[useConversations\] Data returned/,
  /\[MessagesContainer\] Initial load/,
  /\[useInfiniteMessages\] Messages API returned/,
  /\[MessagesList\] Messages received/,
  /\[userIdentity\] Checking user identity/,
  /\[AuthContext\] login: sessionToStore set/,
  /\[AuthContext\] Stytch SDK already initialized/,
  /App initialized/,
  /\[ConversationHeader\] Conversation data received/,
  /\[ContactDetails\] Rendering component/,
  /\[ContactDetails\] Data loaded successfully/,
  /Prefetching contact:/,
  /Already have cached data for contact/,
  // Additional presence error filtering
  /Request failed with status 403.*presence/i,
  /\[API\].*ERROR.*updateMyPresence/i,
  /Uncaught.*promise.*presence/i,
];

// Patterns to always show (errors, warnings, critical info)
const ALWAYS_SHOW_PATTERNS = [
  // Only show specific errors, not all errors
  /error.*critical/i,
  /error.*fatal/i,
  /uncaught/i,
  /exception/i,
  /critical/i,
  // Show specific status codes but exclude presence 403s
  /(?<!presence.*)[45]\d{2}(?!.*presence)/,
];

// Store original console methods
const originalConsole = {
  log: console.log,
  error: console.error,
  warn: console.warn,
  info: console.info,
  debug: console.debug,
};

// Check if a message should be filtered
function shouldFilter(args: unknown[]): boolean {
  const message = args.map(arg => 
    typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
  ).join(' ');
  
  // First check if it should be filtered (takes precedence)
  for (const pattern of FILTER_PATTERNS) {
    if (pattern.test(message)) {
      // Even if it matches a filter pattern, check if it's critical
      for (const alwaysShow of ALWAYS_SHOW_PATTERNS) {
        if (alwaysShow.test(message)) {
          return false; // Don't filter critical errors
        }
      }
      return true; // Filter non-critical matches
    }
  }
  
  // If no filter pattern matched, show it
  return false;
}

// Initialize console filtering
export function initConsoleFilter() {
  if (config.env === 'production') {
    // In production, suppress all but errors
    console.log = () => {};
    console.info = () => {};
    console.debug = () => {};
    return;
  }
  
  // In development, apply smart filtering
  console.log = (...args: unknown[]) => {
    if (!shouldFilter(args)) {
      originalConsole.log(...args);
    }
  };
  
  console.info = (...args: unknown[]) => {
    if (!shouldFilter(args)) {
      originalConsole.info(...args);
    }
  };
  
  console.debug = (...args: unknown[]) => {
    if (!shouldFilter(args)) {
      originalConsole.debug(...args);
    }
  };
  
  // Always show warnings and errors
  console.warn = originalConsole.warn;
  console.error = originalConsole.error;
}

// Allow runtime control
export function setConsoleFilter(enabled: boolean) {
  if (enabled) {
    initConsoleFilter();
  } else {
    // Restore original console
    console.log = originalConsole.log;
    console.error = originalConsole.error;
    console.warn = originalConsole.warn;
    console.info = originalConsole.info;
    console.debug = originalConsole.debug;
  }
}

// Export for debugging - show what would be filtered
export function testConsoleFilter(message: string) {
  return shouldFilter([message]);
}
